import {
  Box,
  Group,
  MultiSelect,
  NumberInput,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedCallback } from '@mantine/hooks';
import { i18nKeys, useTranslation } from 'locales';
import { isIncludedIn } from 'remeda';
import { Icon, IconName } from 'shared/components/Icon';
import type { TableFilter } from 'types/views';
import { getFilterDescriptor } from '../filters/filterDescriptors';
import {
  EMPTY_OPERATOR,
  FILLED_OPERATOR,
  MAX_PREFIX,
  MIN_PREFIX,
  RANGE_END_SUFFIX,
  RANGE_START_SUFFIX,
} from '../filters/filterGrammar';
import type { SelectOption } from '../hooks/useFilterOptionsLoader';
import { readConstantOptions, type useTableFilterState } from '../hooks/useTableFilterState';

// A select filter's stored value may be an array or a scalar (a single choice
// round-trips through the URL as a scalar). MultiSelect needs a string[].
const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];

// Filter values round-trip through the URL as strings; narrow safely for inputs.
const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

type FilterState = ReturnType<typeof useTableFilterState>;

type Props = {
  currentFilter?: TableFilter;
  operator: string;
  setOperator: FilterState['setOperator'];
  onBack: () => void;
  filters: FilterState['filters'];
  setFilterValue: FilterState['setFilterValue'];
  selectSubmit: FilterState['selectSubmit'];
  constants: FilterState['constants'];
  loadOptions: (filter: TableFilter) => (search?: string) => Promise<void>;
  listData: Record<string, SelectOption[]>;
};

// The right-hand pane that edits the selected filter: a value input chosen by
// filter_type plus the empty/filled operator. Values are read from and written to
// the parent's `filters` state directly — no form library.
export function FilterEditor({
  currentFilter,
  operator,
  setOperator,
  onBack,
  filters,
  setFilterValue,
  selectSubmit,
  constants,
  loadOptions,
  listData,
}: Props) {
  const { t } = useTranslation();

  const searchOptions = useDebouncedCallback(
    (filter: TableFilter, search: string) => loadOptions(filter)(search),
    200,
  );

  const handleDateChange =
    (suffix: typeof RANGE_START_SUFFIX | typeof RANGE_END_SUFFIX) => (value: string | null) =>
      setFilterValue(`${currentFilter?.name}${suffix}`, value);

  const handleNumberChange =
    (prefix: typeof MIN_PREFIX | typeof MAX_PREFIX) => (value: number | string) =>
      setFilterValue(`${prefix}${currentFilter?.name}`, value === '' ? '' : String(value));

  return (
    <Box>
      {currentFilter && (
        <Box>
          <UnstyledButton onClick={onBack} mb="md">
            <Group gap={4} align="center">
              <Icon name={IconName.MINIMAL_LEFT} />
              <Text size="lg">{t(i18nKeys.BACK)}</Text>
            </Group>
          </UnstyledButton>
          {isIncludedIn(currentFilter.filter_type, [
            'string',
            'select',
            'boolean',
            'date',
            'number',
          ]) && (
            <>
              <Title order={3} c="purple.6">
                {currentFilter.name_translated}
              </Title>
              <Radio.Group value={operator} onChange={setOperator} my="sm">
                <Radio value="value" label={t(i18nKeys.VALUE)} />
              </Radio.Group>
              {operator === 'value' && (
                <Box mb="lg">
                  {(() => {
                    switch (currentFilter.filter_type) {
                      case 'string':
                        return (
                          <TextInput
                            value={readString(filters[currentFilter.name])}
                            placeholder={currentFilter.name_translated}
                            onChange={event =>
                              setFilterValue(currentFilter.name, event.currentTarget.value)
                            }
                          />
                        );
                      case 'select': {
                        const options = currentFilter.is_api
                          ? (listData[currentFilter.name] ?? [])
                          : currentFilter.constant_name
                            ? readConstantOptions(constants, currentFilter.constant_name)
                            : [];
                        return (
                          <MultiSelect
                            placeholder={t(i18nKeys.CLIENT.FILTER.ALL)}
                            searchable
                            clearable
                            data={options.map(option => ({
                              value: String(option.value),
                              label: option.description,
                            }))}
                            value={toStringArray(filters[currentFilter.name])}
                            onChange={selectSubmit(currentFilter)}
                            onDropdownOpen={
                              currentFilter.is_api ? loadOptions(currentFilter) : undefined
                            }
                            onSearchChange={
                              currentFilter.is_api
                                ? search => searchOptions(currentFilter, search)
                                : undefined
                            }
                          />
                        );
                      }
                      case 'date':
                        return (
                          <Group gap="sm">
                            <DatePickerInput
                              clearable
                              placeholder={t(i18nKeys.FORM.START_DATE)}
                              onChange={handleDateChange(RANGE_START_SUFFIX)}
                              value={
                                readString(filters[`${currentFilter.name}${RANGE_START_SUFFIX}`]) ||
                                null
                              }
                              flex={1}
                            />
                            <DatePickerInput
                              clearable
                              placeholder={t(i18nKeys.FORM.END_DATE)}
                              onChange={handleDateChange(RANGE_END_SUFFIX)}
                              value={
                                readString(filters[`${currentFilter.name}${RANGE_END_SUFFIX}`]) ||
                                null
                              }
                              flex={1}
                            />
                          </Group>
                        );
                      case 'number':
                        return (
                          <Group gap="sm">
                            <NumberInput
                              placeholder={t(i18nKeys.FORM.MIN)}
                              value={readString(filters[`${MIN_PREFIX}${currentFilter.name}`])}
                              onChange={handleNumberChange(MIN_PREFIX)}
                              hideControls
                              flex={1}
                            />
                            <NumberInput
                              placeholder={t(i18nKeys.FORM.MAX)}
                              value={readString(filters[`${MAX_PREFIX}${currentFilter.name}`])}
                              onChange={handleNumberChange(MAX_PREFIX)}
                              hideControls
                              flex={1}
                            />
                          </Group>
                        );
                      case 'boolean':
                        return (
                          <Select
                            placeholder={t(i18nKeys.CLIENT.FILTER.ALL)}
                            data={[
                              { value: 'true', label: t(i18nKeys.YES) },
                              { value: 'false', label: t(i18nKeys.NO) },
                            ]}
                            value={readString(filters[currentFilter.name]) || null}
                            onChange={value => setFilterValue(currentFilter.name, value ?? '')}
                            clearable
                          />
                        );
                      default:
                        return null;
                    }
                  })()}
                </Box>
              )}
            </>
          )}
          <Box>
            {!getFilterDescriptor(currentFilter.name).suppressOperators && (
              <Radio.Group value={operator} onChange={setOperator} mt="sm">
                <Stack gap="sm">
                  <Radio value={EMPTY_OPERATOR} label={t(i18nKeys.VIEWS.FILTERS.EMPTY_DATA)} />
                  <Radio value={FILLED_OPERATOR} label={t(i18nKeys.VIEWS.FILTERS.FILL_DATA)} />
                </Stack>
              </Radio.Group>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
